import type { FC } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    CustomerLandingPageContentfulResponse,
    CustomerLandingPageRelayQuery
} from "src/contentful/graphql/customer-landing-page";

import { token } from "@atlaskit/tokens";

import { customerLandingPageRelayQuery } from "@/__generated_contentful__/customerLandingPageRelayQuery.graphql";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { CustomersLandingPageRelayTemplate } from "@/templates/customers/customers-landing-page/customers-landing-page-relay.template";

export type CustomerLandingPageProps = {
    /** Omitted when SSR/CSR Relay preload fails (e.g. Contentful 401). */
    queryRef?: PreloadedQuery<customerLandingPageRelayQuery> | null;
};

const relayUnavailableCss = css({
    margin: "auto",
    maxWidth: "640px",
    paddingBlock: token("space.600"),
    paddingInline: token("space.400")
});

const CustomersLandingRelayUnavailable: FC = () => {
    const intl = useIntl();
    return (
        <div
            css={relayUnavailableCss}
            data-testid="customers-landing-relay-unavailable"
            role="status"
        >
            {intl.formatMessage({
                id: "CustomersLandingPage.relayUnavailable",
                defaultMessage:
                    "This page could not be loaded. Refresh the page or try again later."
            })}
        </div>
    );
};

const CustomersLandingPageWithQuery: FC<{
    queryRef: PreloadedQuery<customerLandingPageRelayQuery>;
}> = ({ queryRef }) => {
    const preloadedQueryResult = usePreloadedQuery(
        CustomerLandingPageRelayQuery,
        queryRef
    );

    const contentfulResponse =
        CustomerLandingPageContentfulResponse(preloadedQueryResult);
    return (
        <ContentNotFoundErrorBoundary>
            <CustomersLandingPageRelayTemplate
                // @ts-ignore
                customerLanding={contentfulResponse}
            />
        </ContentNotFoundErrorBoundary>
    );
};

export const CustomersLandingPage: FC<CustomerLandingPageProps> = ({
    queryRef
}) => {
    if (!queryRef) {
        return <CustomersLandingRelayUnavailable />;
    }

    return <CustomersLandingPageWithQuery queryRef={queryRef} />;
};
